import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import HeaderBg, { STUDIO } from "./HeaderBg";

function outletDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export default function Press({ items = [] }) {
  return (
    <section id="press" className="relative bg-[#0d0d0d] py-24 md:py-40" data-testid="press-section">
      <HeaderBg image={STUDIO.mixer2} />
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal className="mb-14 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="overline text-gold mb-6">In The Press</p>
            <h2 className="headline text-5xl md:text-7xl text-white">Features</h2>
          </div>
          <p className="text-white/50 font-body max-w-sm">Interviews, features, and the stories behind the sound.</p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((p, i) => (
            <motion.a
              key={p.id}
              href={p.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`press-item-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group relative flex flex-col justify-between border border-white/10 bg-obsidian hover:border-gold/50 transition-colors p-8 md:p-10 min-h-[220px] overflow-hidden"
            >
              {p.image && (
                <>
                  <img
                    src={p.image}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 group-hover:scale-105 transition-all duration-700 ease-out"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid={`press-image-${i}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/85 to-obsidian/40" aria-hidden />
                </>
              )}
              <div className="absolute -right-6 -bottom-8 font-display font-700 text-[9rem] leading-none text-white/[0.03] group-hover:text-gold/[0.06] transition-colors select-none">
                {String(i + 1).padStart(2, "0")}
              </div>
              <span className="relative flex items-center gap-3 mb-6" data-testid={`press-outlet-${i}`}>
                {outletDomain(p.url) && (
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${outletDomain(p.url)}&sz=64`}
                    alt={`${p.outlet} logo`}
                    loading="lazy"
                    className="w-7 h-7 rounded-sm object-contain bg-white/90 p-1 shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid={`press-logo-${i}`}
                  />
                )}
                <span className="font-display uppercase tracking-[0.24em] text-xs text-gold">{p.outlet}</span>
              </span>
              <h3 className="relative font-editorial italic text-2xl md:text-3xl text-white/90 leading-snug flex-1">{p.title}</h3>
              <span className="relative mt-8 inline-flex items-center gap-2 font-display uppercase tracking-[0.15em] text-[0.66rem] text-white/50 group-hover:text-white transition-colors">
                Read Feature <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
